import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Banner, Button, Card, Field, Input, Textarea } from "@magarac/ui";
import type { SettingsInvoicing } from "@magarac/shared/schemas/settings";
import { HelpIcon } from "../../../components/HelpIcon";
import { messageFor } from "../../../lib/errors";
import { useSetting } from "../../../lib/settings";
import { useTenant } from "../../../lib/tenant";

export const Route = createFileRoute("/_app/settings/invoicing")({
  component: InvoicingSettingsPage,
});

const DEFAULTS: SettingsInvoicing = {
  taxRateBp: 0,
  taxLabel: "Sales tax",
  quoteValidDays: 30,
  invoiceDueDays: 14,
  footer: "",
};

/** CRM-17..CRM-19: tax, quote and invoice dates, and the terms printed on them. Screen key: settings.invoicing */
function InvoicingSettingsPage() {
  const { active } = useTenant();
  const { query, save, value } = useSetting("invoicing");
  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Quotes and invoices</h1>
        <HelpIcon screen="settings.invoicing" />
      </div>
      {query.isPending ? (
        <p className="text-muted">Loading…</p>
      ) : (
        <InvoicingForm
          key={active?.id ?? "none"}
          initial={value ?? DEFAULTS}
          onSave={(v) => save.mutateAsync(v)}
          busy={save.isPending}
          error={save.error ? messageFor(save.error) : null}
          saved={save.isSuccess}
        />
      )}
    </div>
  );
}

function InvoicingForm({
  initial,
  onSave,
  busy,
  error,
  saved,
}: {
  initial: SettingsInvoicing;
  onSave: (v: SettingsInvoicing) => Promise<unknown>;
  busy: boolean;
  error: string | null;
  saved: boolean;
}) {
  const [form, setForm] = useState(initial);
  const [rate, setRate] = useState(String(initial.taxRateBp / 100));
  const [problem, setProblem] = useState<string | null>(null);
  const submit = () => {
    const pct = Number(rate.replace("%", "").trim() || "0");
    if (!Number.isFinite(pct) || pct < 0 || pct > 25)
      return setProblem("The tax rate must be a percent between 0 and 25, like 8.25.");
    setProblem(null);
    void onSave({ ...form, taxRateBp: Math.round(pct * 100) });
  };
  return (
    <Card>
      {error || problem ? <Banner tone="danger">{problem ?? error}</Banner> : null}
      {saved ? <Banner tone="ok">Saved. New quotes and invoices use these.</Banner> : null}
      <div className="grid gap-x-4 sm:grid-cols-2">
        <Field id="inv-rate" label="Tax rate (%)" hint="Added to the subtotal. 0 for no tax line.">
          <Input
            id="inv-rate"
            inputMode="decimal"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
          />
        </Field>
        <Field
          id="inv-label"
          label="Tax name"
          hint='Shown on quotes and invoices, like "Sales tax".'
        >
          <Input
            id="inv-label"
            maxLength={40}
            value={form.taxLabel}
            onChange={(e) => setForm({ ...form, taxLabel: e.target.value || "Sales tax" })}
          />
        </Field>
        <Field id="inv-valid" label="Quotes are good for (days)">
          <Input
            id="inv-valid"
            type="number"
            min={1}
            max={365}
            value={form.quoteValidDays}
            onChange={(e) =>
              setForm({
                ...form,
                quoteValidDays: Math.min(365, Math.max(1, Number(e.target.value) || 1)),
              })
            }
          />
        </Field>
        <Field id="inv-due" label="Invoices are due after (days)" hint="0 means due when received.">
          <Input
            id="inv-due"
            type="number"
            min={0}
            max={120}
            value={form.invoiceDueDays}
            onChange={(e) =>
              setForm({
                ...form,
                invoiceDueDays: Math.min(120, Math.max(0, Number(e.target.value) || 0)),
              })
            }
          />
        </Field>
      </div>
      <Field
        id="inv-footer"
        label="Terms printed at the bottom"
        hint="Payment terms, warranty, license number. Up to 500 characters."
      >
        <Textarea
          id="inv-footer"
          rows={3}
          maxLength={500}
          value={form.footer}
          onChange={(e) => setForm({ ...form, footer: e.target.value })}
        />
      </Field>
      <p className="mb-3 text-sm text-muted">
        Changing the tax rate does not change quotes or invoices already sent; it applies when you
        next save one.
      </p>
      <Button busy={busy} onClick={submit}>
        Save
      </Button>
    </Card>
  );
}
